import { clsx } from "clsx";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useAgentsForSelect } from "@/hooks/useAgents";
import type { AgentSelectItem } from "@/types/agent";

interface AgentSelectProps {
  value: string;
  onChange: (agentId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  excludeIds?: string[];
}

export function AgentSelect({
  value,
  onChange,
  placeholder = "Wybierz agenta...",
  disabled = false,
  className,
  excludeIds,
}: AgentSelectProps) {
  const { data: allAgents = [], isLoading } = useAgentsForSelect();
  const agents = useMemo(
    () =>
      excludeIds && excludeIds.length > 0
        ? allAgents.filter((a) => !excludeIds.includes(a.id))
        : allAgents,
    [allAgents, excludeIds],
  );

  const generatedId = useId();
  const listboxId = `${generatedId}-listbox`;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedAgent = agents.find((a) => a.id === value) ?? null;

  const filtered = useMemo<AgentSelectItem[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => a.fullName.toLowerCase().includes(q));
  }, [agents, search]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filtered]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        closeDropdown();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeDropdown]);

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < 280 && rect.top > spaceBelow;
    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 256),
      ...(flipUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const list = listRef.current;
    const item = list?.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightedIndex]);

  function toggleDropdown() {
    if (disabled || isLoading) return;
    if (isOpen) {
      closeDropdown();
    } else {
      setIsOpen(true);
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function selectAgent(agent: AgentSelectItem) {
    onChange(agent.id);
    closeDropdown();
  }

  function handleTriggerKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      toggleDropdown();
    }
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev,
        );
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      }
      case "Enter": {
        e.preventDefault();
        const agent = filtered[highlightedIndex];
        if (agent) selectAgent(agent);
        break;
      }
      case "Escape": {
        e.preventDefault();
        closeDropdown();
        break;
      }
      case "Tab": {
        closeDropdown();
        break;
      }
    }
  }

  return (
    <div ref={containerRef} className={clsx("relative", className)}>
      <div
        className={clsx(
          "flex w-full items-center rounded-md border bg-panel text-sm",
          "transition-colors duration-150",
          disabled && "cursor-not-allowed opacity-50",
          "border-edge-hard",
          "has-[:focus]:border-adara-500 has-[:focus]:ring-1 has-[:focus]:ring-adara-500",
        )}
      >
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={
            isOpen && highlightedIndex >= 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          disabled={disabled}
          onClick={toggleDropdown}
          onKeyDown={handleTriggerKeyDown}
          className="flex min-w-0 flex-1 items-center gap-1 py-1.5 pl-2 pr-1 text-left focus:outline-none"
        >
          <span
            className={clsx(
              "min-w-0 flex-1 truncate text-sm",
              selectedAgent ? "text-main" : "text-faint",
            )}
          >
            {isLoading
              ? "Ładowanie..."
              : (selectedAgent?.fullName ?? placeholder)}
          </span>

          {isLoading && (
            <Loader2
              size={14}
              className="shrink-0 animate-spin text-faint"
              aria-hidden="true"
            />
          )}
        </button>

        {/* Clear button — separate from trigger, no overlap */}
        {selectedAgent && !disabled && (
          <button
            type="button"
            aria-label="Usuń przypisanie"
            tabIndex={-1}
            onClick={() => onChange("")}
            className="shrink-0 rounded p-1 text-faint hover:text-sub focus:outline-none"
          >
            <X size={12} aria-hidden="true" />
          </button>
        )}

        {/* Chevron — always rightmost */}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={clsx(
            "mr-2 shrink-0 text-faint transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="z-[9999] rounded-lg border border-edge bg-panel shadow-lg"
          >
            <div className="border-b border-edge-soft p-2">
              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-faint"
                  aria-hidden="true"
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Szukaj po imieniu lub nazwisku..."
                  className={clsx(
                    "w-full rounded-md border border-edge bg-subtle py-1.5 pl-7 pr-2 text-sm",
                    "placeholder:text-faint focus:border-adara-500 focus:outline-none focus:ring-1 focus:ring-adara-500",
                  )}
                  aria-label="Wyszukaj agenta"
                  aria-autocomplete="list"
                />
              </div>
            </div>

            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label="Lista agentów"
              className="max-h-52 overflow-auto py-1"
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-faint">
                  {search.trim()
                    ? "Brak wyników dla podanej frazy"
                    : "Brak dostępnych agentów"}
                </div>
              ) : (
                filtered.map((agent, index) => {
                  const isSelected = agent.id === value;
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <div
                      key={agent.id}
                      id={`${listboxId}-option-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={-1}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectAgent(agent)}
                      className={clsx(
                        "flex cursor-pointer items-center justify-between px-3 py-2 text-sm",
                        "transition-colors duration-75",
                        isHighlighted && "bg-adara-50 dark:bg-adara-900/30",
                        isSelected
                          ? "font-medium text-adara-700 dark:text-adara-300"
                          : "text-main",
                      )}
                    >
                      <span className="truncate">{agent.fullName}</span>
                      {isSelected && (
                        <Check
                          size={14}
                          aria-hidden="true"
                          className="shrink-0 text-adara-700 dark:text-adara-300"
                        />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
